// src/components/AppointmentNoteEditor.tsx
// Nota del turno editable EN LA TARJETA, sin abrir el turno.
// Se usa en todas las vistas de agenda y en ambos paneles (profesional y
// secretaria), que fue el pedido de Cynthia y la secre de FleboGen.
//
// IMPORTANTE — sobre qué campo edita:
// Edita `notes`: la nota que se carga al crear el turno y la ÚNICA que se
// muestra en las tarjetas de la agenda. El campo `professional_notes` es otro
// (privado, no se muestra en tarjetas); editarlo era la causa del reporte
// "se guarda pero después no lo vemos".

import React, { useState } from 'react';
import { FileText, Pencil, Check, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AppointmentNoteEditorProps {
  appointmentId: number;
  /** Nota actual (campo `notes` del turno) */
  value?: string | null;
  /** true en el panel de secretaria: usa el endpoint scoped de asistente */
  asAssistant?: boolean;
  /** Se dispara con la nota guardada para refrescar la vista del padre */
  onSaved?: (newNotes: string) => void;
  /** Compacto para tarjetas chicas del timeline */
  compact?: boolean;
  className?: string;
}

const AppointmentNoteEditor: React.FC<AppointmentNoteEditorProps> = ({
  appointmentId,
  value,
  asAssistant = false,
  onSaved,
  compact = false,
  className = '',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState(value || '');

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // no abrir el modal del turno al tocar el lápiz
    setDraft(current || '');
    setError(null);
    setEditing(true);
  };

  const cancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(false);
    setDraft(current || '');
    setError(null);
  };

  const save = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const text = draft.trim();
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      const response = asAssistant
        ? await fetch(`${API_URL}/api/assistants/appointments/${appointmentId}/notes`, {
            method: 'PATCH',
            headers,
            body: JSON.stringify({ notes: text }),
          })
        : await fetch(`${API_URL}/api/appointments/appointment/${appointmentId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ notes: text }),
          });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'No se pudo guardar la nota');
      }

      setCurrent(text);
      setEditing(false);
      onSaved?.(text);
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Modo edición
  if (editing) {
    return (
      <div className={`mt-2 ${className}`} onClick={(e) => e.stopPropagation()}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={compact ? 2 : 3}
          autoFocus
          placeholder="Ej: viene 10:30, es por videollamada, trae estudios…"
          className="w-full px-3 py-2 text-xs sm:text-sm text-left border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none bg-white"
        />
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        <div className="flex items-center justify-end gap-2 mt-1.5">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" /> Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    );
  }

  // Con nota: se muestra y se puede editar con el lápiz
  if (current) {
    return (
      <div
        className={`mt-2 text-xs text-left font-semibold bg-amber-100 text-amber-950 px-2.5 py-2 rounded-lg border border-amber-400 flex items-start gap-2 group/note ${className}`}
      >
        <FileText className="w-3.5 h-3.5 mt-px flex-shrink-0 text-amber-700" />
        <span className="flex-1 whitespace-pre-wrap break-words leading-relaxed">{current}</span>
        <button
          type="button"
          onClick={startEdit}
          title="Editar nota"
          className="w-6 h-6 rounded-full bg-amber-200 text-amber-800 border border-amber-400 hover:bg-amber-300 hover:text-amber-900 transition-colors flex items-center justify-center flex-shrink-0"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Sin nota: enlace discreto para agregarla
  return (
    <button
      type="button"
      onClick={startEdit}
      className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-300 hover:bg-amber-100 hover:text-amber-800 hover:border-amber-300 transition-colors ${className}`}
      title="Agregar una nota al turno"
    >
      <FileText className="w-3 h-3" /> Agregar nota
    </button>
  );
};

export default AppointmentNoteEditor;
